<template>
    <div class="c-reservation-summary">
        <div class="c-reservation-summary__divider without-margin-top desktop-only"></div>

        <div class="c-reservation-summary__item">
            <div class="c-reservation-summary__label">
                <span>{{ $t('BranchReservation.Summary.Quantity.title') }}</span>
                <small class="cxs-is-visible">{{ $t('BranchReservation.Summary.Quantity.tooltip') }}</small>
            </div>

            <div class="c-reservation-summary__quantity">
                <r-spinner
                    class="c-form__quantity"
                    :class="{
                        'is-disabled': isQuantityDisabled
                    }"
                    ref="rSpinner"
                >
                    <button type="button" class="c-btn is-decrement" decrement>
                        –
                    </button>

                    <input
                        name="quantity"
                        type="text"
                        class="c-form__input"
                        :value="quantity"
                        autocomplete="off"
                        :min="min"
                        :max="max"
                        :readonly="isQuantityDisabled"
                        @change="onQuantityChange"
                    />

                    <button type="button" class="c-btn is-increment" increment>
                        +
                    </button>
                </r-spinner>

                <i class="ico ico-circle-info js-tippy cxs-is-hidden" v-if="!selectedBranch" :data-tippy-content="$t('BranchReservation.Summary.Quantity.tooltip')" data-z-index="1050"></i>
            </div>
        </div>

        <div class="c-reservation-summary__divider"></div>

        <div class="c-reservation-summary__item c-reservation-summary__item--payment">
            <div class="c-reservation-summary__label">
                {{ $t('BranchReservation.Summary.Payment.title') }}
                <small class="cxs-is-visible">{{ $t('BranchReservation.Summary.Payment.tooltip') }}</small>
                <i class="ico ico-circle-info js-tippy cxs-is-hidden" :data-tippy-content="$t('BranchReservation.Summary.Payment.tooltip')" data-z-index="1050"></i>
            </div>

            <div class="c-reservation-summary__value">
                <strong class="h-is-green">
                    {{ $t('BranchReservation.Summary.Payment.free') }}
                </strong>
            </div>
        </div>

        <div class="c-reservation-summary__divider"></div>

        <ReservationVoucher />

        <div class="c-reservation-summary__divider"></div>

        <div class="c-reservation-summary__item c-reservation-summary__item--price">
            <div class="c-reservation-summary__label">
                {{ $t('BranchReservation.Summary.Price.total') }} {{ $t('withVat') }}
            </div>

            <div class="c-reservation-summary__value is-bigger">
                {{ totals ? formatPrice(totals.price_vat) : '-' }}
            </div>
        </div>

        <div class="c-reservation-summary__item c-reservation-summary__item--price">
            <div class="c-reservation-summary__label is-gray is-smaller">
                {{ $t('BranchReservation.Summary.Price.total') }} {{ $t('withoutVat') }}
            </div>

            <div class="c-reservation-summary__value is-gray">
                {{ totals ? formatPrice(totals.price) : '-' }}
            </div>
        </div>

        <template v-if="totals">
            <div
                class="c-reservation-summary__item c-reservation-summary__item--price"
                v-for="tax in totals.taxes"
                :key="tax.value"
            >
                <div class="c-reservation-summary__label is-gray is-smaller">
                    {{ $t('BranchReservation.Summary.Price.vat') }} {{ tax.value }}%
                </div>

                <div class="c-reservation-summary__value is-gray">
                    {{ formatPrice(tax.price) }}
                </div>
            </div>
        </template>

        <div
            class="c-reservation-summary__item c-reservation-summary__item--price"
            v-if="totals && totals.discount"
        >
            <div class="c-reservation-summary__label is-red is-smaller">
                {{ $t('BranchReservation.Summary.Price.discount') }}
            </div>

            <div class="c-reservation-summary__value is-red">
                {{ formatPrice(totals.discount) }}
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { formatPrice } from 'FrontendJsAssets/common/format'
import ReservationVoucher from 'VueComponents/BranchReservation/ReservationVoucher';
import { tippyAlert, initTippy } from 'Functions/tippy';
import { replaceTranslate } from 'Functions/replaceTranslate';

export default {
    components: {
        ReservationVoucher
    },
    data() {
        return {
            pricesWithVat: window.Eshop.options.pricesWithVat,
            quantity: 1,
            min: 1,
            max: null,
        }
    },
    methods: {
        formatPrice: formatPrice,
        ...mapActions([
            'submitQuantity',
        ]),
        ...mapMutations([
            'updateQuantity',
        ]),
        onQuantityChange() {
            const quantity = this.rSpinner.getParsedValue()
            const spinDirection = this.rSpinner.spinDirection

            if (
                quantity == parseFloat(this.quantity) &&
                quantity == this.max &&
                spinDirection == 'up' || quantity == this.max &&
                spinDirection === null
            ) {
                tippyAlert(
                    this.rSpinner,
                    replaceTranslate(this.$t('BranchReservation.Summary.Quantity.maxReached'), this.selectedBranch.stock),
                    { theme: 'warning' },
                    8000
                )
            }

            this.quantity = quantity
            this.submitQuantity(this.quantity)
        },
    },
    computed: {
        ...mapState([
            'totals',
            'selectedQuantity',
            'isInitialized',
        ]),
        ...mapGetters([
            'selectedBranch'
        ]),
        isQuantityDisabled() {
            return !this.selectedBranch || !this.isInitialized
        },
        rSpinner() {
            return this.$refs.rSpinner
        }
    },
    mounted() {
        if (this.selectedBranch) {
            this.max = this.selectedBranch.stock
        }

        this.$nextTick(() => {
            initTippy()

            this.rSpinner.addEventListener('stop', this.onQuantityChange)
            this.rSpinner.addEventListener('changed', this.onQuantityChange)
        })
    },
    watch: {
        selectedBranch(branch) {
            if (this.quantity > branch.stock) {
                this.quantity = branch.stock

                tippyAlert(
                    this.rSpinner,
                    replaceTranslate(this.$t('BranchReservation.Summary.Quantity.maxChanged'), branch.stock),
                    { theme: 'warning' },
                    8000
                )

                this.submitQuantity(this.quantity)
            }

            this.max = branch.stock
        },
        selectedQuantity(quantity) {
            this.quantity = quantity

            if (this.quantity > this.selectedBranch.stock) {
                this.quantity = this.selectedBranch.stock

                tippyAlert(
                    this.rSpinner,
                    replaceTranslate(this.$t('BranchReservation.Summary.Quantity.maxChanged'), this.selectedBranch.stock),
                    { theme: 'warning' },
                    8000
                )

                this.submitQuantity(this.quantity)
            }
        }
    }
}
</script>